import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@service-victoria/component-library/components/Button/Button';
import Info from '@service-victoria/component-library/components/Notification/Info';
import Body from '@service-victoria/component-library/components/Text/Body';
import React, { useCallback, useImperativeHandle, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import AccordionCheckin from 'Components/AccordionCheckin';
import { SelectedAccordionItem } from 'Components/AccordionCheckin/AccordionCheckin';
import InlineNotification from 'Components/InlineNotification';
import InputField from 'Components/InputField';
import { CallToActionContainer } from 'Components/KioskRegistration/KioskRegistration';
import LinkButton from 'Components/LinkButton';
import { useFeatureToggles, useModal } from 'hooks';
import { schema } from 'schema/checkinSchema';
import { Guest, PrimaryContact, PrimaryContactWithGuests, TrackCheckInEvent } from 'types/checkin';
import { constants } from 'utils';
import { CHECKIN_FIELDS } from 'utils/constants';

import {
  NotificationWrapper,
  GuestsButtonWrapper,
  Form,
  BottomContainer,
  SingleViewWrapper,
  PlusIcon
} from './styled';

const defaultValues: PrimaryContact = {
  firstName: '',
  lastName: '',
  contactNumber: ''
};

interface CheckinFormProps {
  onSubmit: SubmitHandler<PrimaryContactWithGuests>;
  onReset: () => void;
  onSuccessClose: () => void;
  showGenericError?: boolean;
  showSuccess?: boolean;
  isLoading?: boolean;
  trackCheckinEvent: TrackCheckInEvent;
}

export interface CheckinFormHandle {
  resetForm: () => void;
}

const CheckinForm = React.forwardRef<CheckinFormHandle, CheckinFormProps>(function CheckinFormBase(
  { onSubmit, onReset, showGenericError, showSuccess, onSuccessClose, trackCheckinEvent },
  ref
) {
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    formState: { errors, isSubmitting, isValid, touchedFields, isSubmitted, isSubmitSuccessful },
    reset
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema)
  });
  const modal = useModal();

  const [selectedAccordionItem, setSelectedAccordionItem] = useState<SelectedAccordionItem>();

  const showAddAnother = useFeatureToggles('showDependantsMode');

  useImperativeHandle(ref, () => handle.current);

  const [guests, setGuests] = useState<Guest[]>([]);
  const showAccordionView = guests.length > 0;
  const atMaxGuestLimit = guests.length === 10;

  const resetForm = useCallback(() => {
    reset(defaultValues);
    setGuests([]);
    modal.close();
  }, [modal, reset, setGuests]);

  const getCheckinHandle = useCallback(
    (): CheckinFormHandle => ({
      resetForm: () => {
        resetForm();
      }
    }),
    [resetForm]
  );
  const handle = useRef<CheckinFormHandle>(getCheckinHandle());

  /**
   * Using useEffect to reset the form when submit is successful.
   * The reason why manually resetting the form values does not work after we submit
   * is because the form state does not get reset properly (e.g isSubmitted, etc).
   *
   * Also I follow this pattern from the docos: https://react-hook-form.com/api/useform/reset
   */
  React.useEffect(() => {
    if (isSubmitSuccessful) {
      resetForm();
    }
  }, [isSubmitSuccessful, resetForm]);

  const primaryContact = getValues();

  const addGuest = () => {
    setGuests([
      ...guests,
      {
        id: uuid(),
        firstName: '',
        lastName: primaryContact.lastName,
        contactNumber: primaryContact.contactNumber,
        isNew: true
      }
    ]);
  };

  const submitCheckin: SubmitHandler<PrimaryContactWithGuests> = data =>
    onSubmit({
      ...data,
      guests: guests.map(
        ({ id, isContactNumberSetToDefaultValue, isLastNameSetToDefaultValue, isNew, ...guest }) =>
          guest
      )
    });

  const singleCheckinView = () => {
    return (
      <SingleViewWrapper>
        <InputField
          {...register('firstName')}
          {...CHECKIN_FIELDS.FIRST_NAME}
          error={(isSubmitted || touchedFields.firstName) && errors.firstName?.message}
        />
        <InputField
          {...register('lastName')}
          {...CHECKIN_FIELDS.LAST_NAME}
          error={(isSubmitted || touchedFields.lastName) && errors.lastName?.message}
        />
        <InputField
          {...register('contactNumber')}
          {...CHECKIN_FIELDS.CONTACT_NUMBER}
          type='tel'
          error={(isSubmitted || touchedFields.contactNumber) && errors.contactNumber?.message}
        />
      </SingleViewWrapper>
    );
  };

  const syncSingleCheckinView = useCallback(
    (contact: PrimaryContact) => {
      setValue('firstName', contact.firstName);
      setValue('lastName', contact.lastName);
      setValue('contactNumber', contact.contactNumber);
      trigger();
    },
    [setValue, trigger]
  );

  const onSelectedAccordionChange = useCallback((selected: SelectedAccordionItem | undefined) => {
    setSelectedAccordionItem(selected);
  }, []);

  const openClearFormConfirmation = useCallback(() => {
    modal.open(
      {
        children: 'Are you sure you want to clear this form?',
        title: 'Clear form?'
      },
      {
        type: 'CONFIRMATION',
        confirmationOptions: {
          actions: [
            {
              onClick: () => {
                resetForm();
                onReset();
                modal.close();
              },
              label: 'Clear form'
            }
          ]
        }
      }
    );
  }, [modal, onReset, resetForm]);

  const onResetFormClick = useCallback(() => {
    if (showAccordionView) {
      openClearFormConfirmation();
    } else {
      resetForm();
      onReset();
    }
  }, [showAccordionView, openClearFormConfirmation, resetForm, onReset]);

  const elementProps = showAccordionView
    ? {}
    : {
        onSubmit: handleSubmit(submitCheckin),
        action: '#',
        'data-testid': 'Form',
        autoComplete: 'off'
      };

  return (
    <Form
      as={showAccordionView ? 'div' : undefined}
      {...elementProps}
      showAccordionView={showAccordionView}>
      {showAccordionView ? (
        <AccordionCheckin
          guests={guests}
          setGuests={setGuests}
          primaryContact={primaryContact}
          syncSingleCheckinView={syncSingleCheckinView}
          onSelectedAccordionChange={onSelectedAccordionChange}
          disabledOnAccordionItemClick={isSubmitting}
          trackCheckinEvent={trackCheckinEvent}
        />
      ) : (
        singleCheckinView()
      )}
      {!showAddAnother && (
        <>
          <InlineNotification
            visible={showSuccess}
            message={constants.CHECKED_IN_MSG}
            onClose={() => {
              onSuccessClose();
            }}
          />
          {showGenericError && (
            <NotificationWrapper data-testid='InfoNotification'>
              <Info value={[`Sorry, there's a problem sending the data.`, `Please try again.`]} />
            </NotificationWrapper>
          )}
        </>
      )}

      {!selectedAccordionItem && (
        <BottomContainer showAccordionView={showAccordionView}>
          {showAddAnother && (
            <GuestsButtonWrapper>
              <Button
                buttonType='secondary'
                onClick={addGuest}
                disabled={!isValid || atMaxGuestLimit || isSubmitting}
                data-testid='AddAnotherButton'>
                <PlusIcon />
                Add another
              </Button>
              <Body>
                Add people who are unable to check themselves in. Make sure it&apos;s okay with them
                first.
              </Body>

              <InlineNotification
                visible={showSuccess}
                message={constants.CHECKED_IN_MSG}
                onClose={() => {
                  onSuccessClose();
                }}
              />
              {atMaxGuestLimit && (
                <Info
                  value='You have reached the maximum of 10 additional individuals'
                  textProps={{
                    'data-testid': 'MaxGuestWarningInfo'
                  }}
                />
              )}
              {showGenericError && (
                <Info
                  value={[`Sorry, there's a problem sending the data.`, `Please try again.`]}
                  textProps={{
                    'data-testid': 'InfoNotification'
                  }}
                />
              )}
            </GuestsButtonWrapper>
          )}

          <CallToActionContainer>
            <Button
              type='submit'
              disabled={isSubmitting || showSuccess}
              isLoading={isSubmitting}
              onClick={handleSubmit(submitCheckin)}
              data-testid='CheckinSubmitButton'>
              Check-in
            </Button>
            <LinkButton primary onClick={onResetFormClick} data-testid='CheckinClearFormButton'>
              Clear form
            </LinkButton>
          </CallToActionContainer>
        </BottomContainer>
      )}
    </Form>
  );
});

export default CheckinForm;
